import React, { useState, useEffect } from "react";
import TagInputSignUp from "../../../UI/Inputs/TagInputSignUp";
import { useTranslation } from "react-i18next";
import { checkPasswordValidations } from "../../../../helpers";
import InfosPasswordLabels from "../../../InfosLabels/InfosPasswordLabels";
import RoundedButton from "../../../Buttons/RoundedButton";
import {
  checkValidationsBt,
  validarPatternEmail,
  validarPatternEmailProvidor,
} from "../../../../helpers";
import { Auth } from "aws-amplify";
import InfoCircle from "../../../../assets/images/icons/Icon_info_circle.svg";
import { Box } from "@mui/material";
import TermsAndPrivacyLinks from "./TermsAndPrivacyLinks";
import RoundedButtonOutlined from "components/Buttons/RoundedButtonOutlined";

/**
 * Componente para etapa de entrada de dados de login (e-mail e senha)
 * no processo de cadastro do usuário
 * @param {Object} props - Propriedades do componente
 * @returns {JSX.Element} Formulário de dados de login
 */
const LoginData = (props) => {
  const { t } = useTranslation(); // Hook de internacionalização para traduções

  // Estados para gerenciar valores dos campos e validações
  const [pw, setPW] = useState(""); // Estado para a senha
  const [confirmPw, setConfirmPW] = useState(""); // Estado para confirmação de senha
  const [userExist, setUserExist] = useState(false); // Indica se o usuário já existe
  const [showTextError, setShowTextError] = useState(false); // Controla exibição de mensagens de erro
  const [hasDisable, setHasDisable] = useState(true); // Controla se o botão próximo está desabilitado

  const [touchedConfirmPw, setTouchedConfirmPw] = useState(false); // Estado para controlar input de confirmação de senha

  // Objeto com resultados da validação de senha
  const { hasLowercase, hasUppercase, hasNumber, hasMinEight } =
    checkPasswordValidations(pw);

  // Objeto com resultados de outras validações do formulário
  const { isEmail, allRequirementsOk, isSamePassword } = checkValidationsBt(
    props.signUpForm.username,
    pw,
    confirmPw,
    userExist,
  );

  const [errorEmail, setErrorEmail] = useState(""); // Estado para mensagem de erro de email
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // Regex para validação básica de email

  // Estado para controlar validação de email ("", "warn" ou "error")
  const [isValidEmail, setIsValidEmail] = useState(
    props.signUpForm.username !== ""
      ? emailRegex.test(props.signUpForm.username)
        ? ""
        : "error"
      : "",
  );

  /**
   * Efeito para verificar automaticamente o email quando ele vem preenchido por parâmetro
   * ou quando é alterado via props
   */
  useEffect(() => {
    // Só faz a verificação se o email estiver preenchido
    if (props.signUpForm.username !== "") {
      // Executa a verificação do email assincronamente
      check_email_Server(null, (status) => {
        // Após a verificação, atualiza a interface conforme necessário
        console.log("Email verificado automaticamente:", status);
      });
    }
  }, [props.signUpForm.username]); // Executa quando o email muda

  /**
   * Efeito para controlar habilitação do botão próximo
   * e atualizar estados de senha
   */
  useEffect(() => {
    // Verifica se todas as validações de senha passaram, se as senhas conferem
    // e se o email é válido ou tem apenas alerta (não erro)
    if (
      Object.values(checkPasswordValidations(pw)).every(
        (item) => item === true,
      ) &&
      props.signUpForm.confirm_password &&
      props.signUpForm.confirm_password === props.signUpForm.password &&
      ["", "warn"].indexOf(isValidEmail) > -1
    ) {
      setHasDisable(false); // Habilita o botão
    } else {
      setHasDisable(true); // Desabilita o botão
    }

    // Atualiza estados locais com valores do formulário
    setPW(props.signUpForm.password);
    checkPasswordValidations(pw);
    setConfirmPW(props.signUpForm.confirm_password);

    // Se o componente está sendo recarregado, mostra mensagens de erro
    props.reload && setShowTextError(true);
  }, [
    pw,
    props.reload,
    props.signUpForm.confirm_password,
    isValidEmail,
    props.signUpForm.password,
  ]);

  /**
   * Efeito para mostrar mensagens de erro quando a senha atender todos os requisitos
   */
  useEffect(() => {
    if (
      Object.values(checkPasswordValidations(pw)).every((item) => item === true)
    ) {
      setShowTextError(true);
    }
  }, [hasDisable]);

  /**
   * Função para avançar para próxima etapa após verificação de email
   */
  const NextPage = () => {
    setTimeout(() => {
      check_email_Server(null, NextStepPage);
    }, 100);
  };

  /**
   * Função que processa o resultado da verificação de email e avança se tudo estiver válido
   * @param {string} emailUserExist - Resultado da verificação de existência do email
   * @returns {number|undefined} Retorna 0 se houver erro, undefined caso contrário
   */
  const NextStepPage = (emailUserExist) => {
    // Verifica se o email já existe ou é inválido
    const userExistCheck =
      emailUserExist === "UserExist" || emailUserExist === "UserWrongEmail";

    if (userExistCheck) {
      setShowTextError(true);
      return 0;
    }

    const pw = props.signUpForm.password;
    const confirmPw = props.signUpForm.confirm_password;

    // Verifica se todas as validações de senha passaram
    const passwordValidations = Object.values(checkPasswordValidations(pw));
    const passwordIsValid = passwordValidations.every((item) => item === true);

    // Verifica outras validações do formulário
    const validationsBt = checkValidationsBt(
      props.signUpForm.username,
      pw,
      confirmPw,
      userExistCheck,
    );
    const otherValidations = Object.values(validationsBt);
    const otherValidationsAreValid = otherValidations.every(
      (item) => item === true,
    );

    // Se todas as validações passaram, avança para a próxima etapa
    if (passwordIsValid && otherValidationsAreValid) {
      props.setStep("Information");
    }
  };

  /**
   * Mapeamento de erros do servidor para ações correspondentes
   */
  const map_errors_email_server = {
    // Ação para quando o usuário já existe
    UsernameExistsException: () => {
      setUserExist(true);
      setErrorEmail(t("global.email_address_exist"));
    },
    // Ação padrão para outros casos
    Default: () => {
      setUserExist(false);
      if (errorEmail == t("global.email_address_exist")) setErrorEmail("");
    },
  };

  // Estado para armazenar sugestão de correção para o email
  const [suggestedEmail, setSuggestedEmail] = useState(null);

  /**
   * Função para verificar a validade do email no servidor
   * @param {Event} event - Evento que disparou a função (pode ser null)
   * @param {Function} call_back - Função de callback após verificação
   * @returns {number|undefined} Retorna 0 em caso de erro, undefined caso contrário
   */
  const check_email_Server = (event, call_back = () => {}) => {
    // Se o email estiver vazio, não faz a verificação
    if (!props.signUpForm.username) {
      setIsValidEmail("");
      setErrorEmail("");
      call_back("");
      return 0;
    }

    // Verifica se o email segue o formato básico
    let _isInvalidEmail = emailRegex.test(props.signUpForm.username);

    if (!_isInvalidEmail) {
      setErrorEmail(t("global.enter_valid_email_address"));
      setIsValidEmail("error");
      setSuggestedEmail("");
      call_back("UserWrongEmail");
      return 0;
    }

    // Divide o email em nome e provedor
    const emailSplited = props.signUpForm.username.split("@");

    // Verifica se o provedor está na lista de bloqueados
    const blockemail = validarPatternEmailProvidor(emailSplited[1]);
    if (blockemail) {
      setSuggestedEmail(emailSplited[0] + "@" + blockemail);
      setErrorEmail(t("global.email_address_pattern_error"));
      setIsValidEmail("error");
      call_back("UserWrongEmail");
      return 0;
    }

    // Verifica se há sugestão de correção para o provedor
    const providorSuggested = validarPatternEmail(emailSplited[1]);
    if (providorSuggested) {
      setSuggestedEmail(emailSplited[0] + "@" + providorSuggested);
      setErrorEmail(t("global.email_address_pattern_warn"));
      setIsValidEmail("warn");
      call_back("UserSuggestedEmail");
    } else {
      setErrorEmail("");
      setIsValidEmail("");
    }

    // Tenta registrar o usuário para verificar se o email já existe
    let _signUpUser = {
      username: props.signUpForm.username,
      password: "SenhaDefault123", // Senha temporária apenas para teste
    };

    Auth.signUp(_signUpUser)
      .then((res) => {
        console.log(res);
        call_back("UserNotExist");
      })
      .catch((errors) => {
        console.log(errors.name);
        const type_error = errors.name;
        if (type_error in map_errors_email_server) {
          map_errors_email_server[type_error]();
          call_back("UserExist");
        } else {
          call_back("UserNotExist");
          map_errors_email_server["Default"]();
        }
      });
  };

  return (
    <Box
      component={"form"}
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        justifyContent: "space-between",
        width: "100%",
      }}
    >
      {/* Seção para usuário vinculado a um profissional */}
      {props.parent && (
        <Box
          sx={{
            display: "flex",
            borderRadius: "16px",
            border: "1px solid #a7c5fd",
            padding: "14px 22px",
            background: "#f1f6fd",
          }}
        >
          <img
            src={InfoCircle}
            alt=""
            style={{ width: "20px", marginRight: "15px" }}
          />
          <Box sx={{ fontSize: "14px", color: "#181818" }}>
            {t("global.you_are_being_linked_by_the_professional")}
            {props.parent.given_name}
          </Box>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          width: "100%",
          mt: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            width: "100%",
          }}
        >
          {/* Campo de entrada para email */}
          <TagInputSignUp
            sx={{
              width: "100%",
            }}
            type="email"
            correctionSafari={true}
            name="username"
            label={t("global.email")}
            placeholder={t("global.email")}
            value={props.signUpForm.username}
            onChange={(event) => {
              props.signUpForm.username = event.target.value;
              props.onChange(event);
            }}
            onBlur={check_email_Server}
            errorButton={!!errorEmail}
            suggestedText={suggestedEmail}
            typeError={isValidEmail}
            showTextError={!!errorEmail}
            textError={errorEmail}
            required
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 0, md: 10 },
            width: "100%",
          }}
        >
          {/* Campo de entrada para senha */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <TagInputSignUp
              type="password"
              name="password"
              label={t("global.password")}
              placeholder={t("global.password")}
              value={props.signUpForm.password}
              onChange={(t) => {
                setPW(t.target.value);
                props.onChange(t);
              }}
              errorButton={!allRequirementsOk}
              showTextError={showTextError}
              textError={t("global.please_check_that_your_password")}
              required
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Campo de entrada para confirmação de senha */}
            <TagInputSignUp
              type="password"
              name="confirm_password"
              label={t("global.confirm_password")}
              placeholder={t("global.confirm_password")}
              value={props.signUpForm.confirm_password}
              onChange={(t) => {
                setConfirmPW(t.target.value);
                props.onChange(t);
              }}
              errorButton={touchedConfirmPw && !isSamePassword}
              showTextError={touchedConfirmPw && !isSamePassword}
              onBlur={() => setTouchedConfirmPw(true)}
              textError={t("global.the_passwords_you_entered_dont_match")}
              required
            />
          </Box>
        </Box>
      </Box>

      {/* Componente que mostra os requisitos de senha e seu status */}
      <InfosPasswordLabels
        hasLowercase={hasLowercase}
        hasUppercase={hasUppercase}
        hasNumber={hasNumber}
        hasMinEight={hasMinEight}
      />

      {/* Seção de termos e políticas */}
      <TermsAndPrivacyLinks />

      {/* Botões de navegação */}
      <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 2 }}>
        {/* Botão voltar */}
        <RoundedButtonOutlined
          text={t("global.back")}
          onClick={() => props.setStep("Profile")}
          sx={{
            width: " 200px",
          }}
        />

        {/* Botão próximo */}
        <RoundedButton
          disable={hasDisable}
          text={t("global.next")}
          sx={{
            width: " 200px",
            cursor: hasDisable ? "not-allowed" : "pointer",
          }}
          onClick={() => {
            NextPage();
          }}
        />
      </Box>
    </Box>
  );
};

export default LoginData;
